import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminViewModel } from './useAdminViewModel';
import { Logo } from '../../components/Logo';
import { copyProjectToOwner } from '../../services/projects';
import {
  AdminPerson,
  AdminReviewer,
  adminSetProjectReviewEnabled,
  adminSetProjectTags,
  adminUpsertReviewer,
  ensureAdminProjectCommentAccess,
  updateReviewRequestStatus,
} from '../../services/admin';
import { PROJECT_DOMAINS, ProjectDomain, ReviewRequest, ReviewStatus } from '../../types';
import {
  Copy,
  FileText,
  Users,
  FolderOpen,
  LogOut,
  ArrowLeft,
  Loader2,
  ClipboardCheck,
  MessageSquare,
  UserRound,
  Search,
  MoreVertical,
  Plus,
  Pencil,
  Shield,
  Tag,
  X,
} from 'lucide-react';

const PEOPLE_PAGE_SIZE = 25;
const STATUS_OPTIONS: ReviewStatus[] = [
  'waiting_for_review',
  'in_review',
  'approved',
  'needs_work',
  'complete',
  'removed',
];
type AdminTab =
  | 'projects'
  | 'people'
  | 'reviewers'
  | 'reviewQueue'
  | 'approvedProjects'
  | 'completedProjects';

export function AdminPage() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    busy,
    stats,
    permissions,
    projects,
    reviewQueue,
    approvedProjects,
    completedProjects,
    reviewers,
    people,
    peoplePageIndex,
    peopleLoading,
    peopleError,
    canLoadPreviousPeoplePage,
    canLoadNextPeoplePage,
    loadNextPeoplePage,
    loadPreviousPeoplePage,
    error,
    initialize,
  } = useAdminViewModel();
  const [copyingProjectId, setCopyingProjectId] = useState<string | null>(null);
  const [openingReviewProjectId, setOpeningReviewProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('projects');
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    reviewId: string;
    status: ReviewStatus;
    projectTitle: string;
  } | null>(null);
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [updatingReviewId, setUpdatingReviewId] = useState<string | null>(null);
  const [tableSearch, setTableSearch] = useState('');
  const [activeProjectMenu, setActiveProjectMenu] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [editingTagsFor, setEditingTagsFor] = useState<{ projectId: string; currentTags: string[] } | null>(null);
  const [tagsDraft, setTagsDraft] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [viewingSubmitterNotes, setViewingSubmitterNotes] = useState<ReviewRequest | null>(null);
  const [editingStatusReviewId, setEditingStatusReviewId] = useState<string | null>(null);
  const [showAddReviewerModal, setShowAddReviewerModal] = useState(false);
  const [reviewerEmailDraft, setReviewerEmailDraft] = useState('');
  const [reviewerDomainsDraft, setReviewerDomainsDraft] = useState<ProjectDomain[]>([]);
  const [savingReviewer, setSavingReviewer] = useState(false);
  const [reviewerError, setReviewerError] = useState<string | null>(null);
  const requestedTab = location.state && typeof location.state === 'object' && 'initialTab' in location.state
    ? location.state.initialTab
    : null;

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (requestedTab === 'projects' && permissions.canViewAllProjects) {
      setActiveTab('projects');
      return;
    }

    if (requestedTab === 'people' && permissions.canViewPeople) {
      setActiveTab('people');
      return;
    }

    if (requestedTab === 'reviewers' && permissions.canViewReviewers) {
      setActiveTab('reviewers');
      return;
    }

    if (requestedTab === 'approvedProjects' && permissions.canViewApprovedProjects) {
      setActiveTab('approvedProjects');
      return;
    }

    if (requestedTab === 'completedProjects' && permissions.canViewApprovedProjects) {
      setActiveTab('completedProjects');
      return;
    }

    if (requestedTab === 'reviewQueue' && permissions.canViewReviewQueue) {
      setActiveTab('reviewQueue');
      return;
    }

    setActiveTab((currentTab) => {
      if (currentTab === 'projects' && permissions.canViewAllProjects) {
        return currentTab;
      }

      if (currentTab === 'people' && permissions.canViewPeople) {
        return currentTab;
      }

      if (currentTab === 'reviewers' && permissions.canViewReviewers) {
        return currentTab;
      }

      if (currentTab === 'reviewQueue' && permissions.canViewReviewQueue) {
        return currentTab;
      }

      if (currentTab === 'approvedProjects' && permissions.canViewApprovedProjects) {
        return currentTab;
      }

      if (currentTab === 'completedProjects' && permissions.canViewApprovedProjects) {
        return currentTab;
      }

      if (permissions.canViewPeople) {
        return 'people';
      }

      if (permissions.canViewReviewers) {
        return 'reviewers';
      }

      if (permissions.canViewReviewQueue) {
        return 'reviewQueue';
      }

      return permissions.canViewAllProjects ? 'projects' : currentTab;
    });
  }, [
    permissions.canViewAllProjects,
    permissions.canViewApprovedProjects,
    permissions.canViewPeople,
    permissions.canViewReviewers,
    permissions.canViewReviewQueue,
    requestedTab,
  ]);

  useEffect(() => {
    if (!activeProjectMenu) return;

    const handleClick = () => setActiveProjectMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [activeProjectMenu]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Failed to logout:', err);
    }
  }

  function formatSize(chars: number | null): string {
    if (chars === null) return '—';
    const kb = chars / 1024;
    return kb < 1 ? `${chars} B` : `~${kb.toFixed(1)} KB`;
  }

  function formatStatus(status: ReviewStatus): string {
    switch (status) {
      case 'waiting_for_review':
        return 'Waiting for Review';
      case 'in_review':
        return 'In Review';
      case 'approved':
        return 'Approved Assessment';
      case 'needs_work':
        return 'Needs Work';
      case 'complete':
        return 'Complete';
      case 'removed':
        return 'Remove from queue';
      default:
        return status;
    }
  }

  const projectsInReviewCount = reviewQueue.filter(
    (review) => review.status === 'in_review'
  ).length;
  const projectsNeedingReviewCount = reviewQueue.filter(
    (review) => review.status === 'waiting_for_review' || review.status === 'needs_work'
  ).length;
  const normalizedSearch = tableSearch.trim().toLowerCase();

  // Collect all unique tags across projects and review queue for the filter pills
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    projects.forEach((p) => p.tags?.forEach((t) => tagSet.add(t)));
    reviewQueue.forEach((r) => r.tags?.forEach((t) => tagSet.add(t)));
    approvedProjects.forEach((r) => r.tags?.forEach((t) => tagSet.add(t)));
    completedProjects.forEach((r) => r.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [projects, reviewQueue, approvedProjects, completedProjects]);

  const filteredProjects = projects.filter((project) => {
    if (tagFilter && !(project.tags || []).includes(tagFilter)) return false;
    if (!normalizedSearch) return true;
    return [
      project.title,
      project.ownerName,
      project.ownerId,
      project.updatedAt || '',
      ...(project.tags || []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  const filteredPeople = people.filter((person) => {
    if (!normalizedSearch) return true;
    return [
      person.name,
      person.email,
      String(person.projectCount),
      person.lastLoginAt ? formatLastLogin(person) : '',
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  const filteredReviewers = reviewers.filter((reviewer) => {
    if (!normalizedSearch) return true;
    return [
      reviewer.name,
      reviewer.email,
      ...reviewer.domains,
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  const filteredReviewQueue = reviewQueue.filter((review) => {
    if (tagFilter && !(review.tags || []).includes(tagFilter)) return false;
    if (!normalizedSearch) return true;
    return [
      review.projectTitle,
      review.submitterName,
      review.ownerId,
      review.reviewedByName || '',
      review.submitterNotes || '',
      ...(review.noteEntries || []).map((note) => note.text),
      ...(review.noteEntries || []).map((note) => note.authorName),
      formatStatus(review.status),
      ...(review.tags || []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  const filteredApprovedProjects = approvedProjects.filter((review) => {
    if (tagFilter && !(review.tags || []).includes(tagFilter)) return false;
    if (!normalizedSearch) return true;
    return [
      review.projectTitle,
      review.submitterName,
      review.ownerId,
      review.reviewedByName || '',
      review.submitterNotes || '',
      ...(review.noteEntries || []).map((note) => note.text),
      ...(review.noteEntries || []).map((note) => note.authorName),
      formatStatus(review.status),
      ...(review.tags || []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  const filteredCompletedProjects = completedProjects.filter((review) => {
    if (tagFilter && !(review.tags || []).includes(tagFilter)) return false;
    if (!normalizedSearch) return true;
    return [
      review.projectTitle,
      review.submitterName,
      review.ownerId,
      review.reviewedByName || '',
      review.submitterNotes || '',
      ...(review.noteEntries || []).map((note) => note.text),
      ...(review.noteEntries || []).map((note) => note.authorName),
      formatStatus(review.status),
      ...(review.tags || []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  const viewingNoteEntries = useMemo(
    () => [...(viewingSubmitterNotes?.noteEntries || [])].sort(
      (left, right) => left.createdAt.getTime() - right.createdAt.getTime()
    ),
    [viewingSubmitterNotes]
  );
  const peopleRangeStart = people.length > 0
    ? peoplePageIndex * PEOPLE_PAGE_SIZE + 1
    : 0;
  const peopleRangeEnd = peopleRangeStart > 0
    ? peopleRangeStart + people.length - 1
    : 0;

  async function handleCopyProject(
    event: React.MouseEvent<HTMLButtonElement>,
    projectId: string,
    title: string
  ) {
    event.stopPropagation();
    if (!currentUser) return;

    setCopyingProjectId(projectId);
    try {
      const copiedProjectId = await copyProjectToOwner(
        projectId,
        currentUser.uid,
        `Copy of ${title}`
      );
      navigate(`/project/${copiedProjectId}`);
    } catch (err) {
      console.error('Failed to copy admin project:', err);
    } finally {
      setCopyingProjectId(null);
    }
  }

  async function handleSetProjectReviewsEnabled(
    event: React.MouseEvent<HTMLButtonElement>,
    projectId: string,
    reviewEnabled: boolean
  ) {
    event.stopPropagation();

    try {
      await adminSetProjectReviewEnabled(projectId, reviewEnabled);
      await initialize();
    } catch (err) {
      console.error('Failed to update review settings:', err);
    } finally {
      setActiveProjectMenu(null);
    }
  }

  async function handleOpenReviewProject(review: ReviewRequest) {
    setOpeningReviewProjectId(review.projectId);
    try {
      await ensureAdminProjectCommentAccess(review.projectId);
      navigate(`/project/${review.projectId}`, {
        state: {
          backTarget: {
            pathname: '/admin',
            state: { initialTab: activeTab },
          },
        },
      });
    } catch (err) {
      console.error('Failed to open review project:', err);
    } finally {
      setOpeningReviewProjectId(null);
    }
  }

  function formatLastLogin(person: AdminPerson): string {
    if (!person.lastLoginAt) return '—';
    return person.lastLoginAt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function formatNoteTimestamp(date: Date): string {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function formatApprovedDate(date: Date): string {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function formatReviewQueueSubtitle(review: ReviewRequest): string {
    if (review.hasPreviousApproval) {
      return 'Needs final approval';
    }

    const previousReviewCount = review.previousReviewCount ?? 0;
    const reviewLabel = previousReviewCount === 1 ? 'review' : 'reviews';
    return `Needs assessment review, ${previousReviewCount} previous ${reviewLabel}`;
  }

  function formatApprovedProjectSubtitle(review: ReviewRequest): string {
    return `Approved: ${formatApprovedDate(review.reviewedAt || review.updatedAt)}`;
  }

  function openTagEditor(projectId: string, currentTags: string[]) {
    setEditingTagsFor({ projectId, currentTags });
    setTagsDraft(currentTags.join(', '));
  }

  function resetReviewerModal() {
    setShowAddReviewerModal(false);
    setReviewerEmailDraft('');
    setReviewerDomainsDraft([]);
    setReviewerError(null);
    setSavingReviewer(false);
  }

  function toggleReviewerDomain(domain: ProjectDomain) {
    setReviewerDomainsDraft((currentDomains) => (
      currentDomains.includes(domain)
        ? currentDomains.filter((currentDomain) => currentDomain !== domain)
        : [...currentDomains, domain]
    ));
  }

  async function handleSaveTags() {
    if (!editingTagsFor) return;

    setSavingTags(true);
    try {
      const parsed = tagsDraft
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      await adminSetProjectTags(editingTagsFor.projectId, parsed);
      setEditingTagsFor(null);
      setTagsDraft('');
      await initialize();
    } catch (err) {
      console.error('Failed to save tags:', err);
    } finally {
      setSavingTags(false);
    }
  }

  async function applyReviewStatusChange(
    reviewId: string,
    status: ReviewStatus,
    notes = ''
  ) {
    setUpdatingReviewId(reviewId);
    try {
      await updateReviewRequestStatus({
        reviewId,
        status,
        notes,
      });
      setEditingStatusReviewId(null);
      setPendingStatusChange(null);
      setAdminNotesDraft('');
      await initialize();
    } catch (err) {
      console.error('Failed to update review status:', err);
    } finally {
      setUpdatingReviewId(null);
    }
  }

  async function handleQueueStatusSelect(
    review: ReviewRequest,
    status: ReviewStatus
  ) {
    setEditingStatusReviewId(null);
    if (status === review.status) return;

    if (status === 'in_review') {
      await applyReviewStatusChange(review.id, status);
      return;
    }

    setPendingStatusChange({
      reviewId: review.id,
      status,
      projectTitle: review.projectTitle,
    });
    setAdminNotesDraft('');
  }

  async function handleConfirmStatusChange() {
    if (!pendingStatusChange) return;
    await applyReviewStatusChange(
      pendingStatusChange.reviewId,
      pendingStatusChange.status,
      adminNotesDraft
    );
  }

  async function handleAddReviewer() {
    const normalizedEmail = reviewerEmailDraft.trim().toLowerCase();
    if (!normalizedEmail) {
      setReviewerError('Email is required.');
      return;
    }

    if (reviewerDomainsDraft.length === 0) {
      setReviewerError('Select at least one review domain.');
      return;
    }

    setSavingReviewer(true);
    setReviewerError(null);
    try {
      await adminUpsertReviewer({
        email: normalizedEmail,
        domains: reviewerDomainsDraft,
      });
      resetReviewerModal();
      await initialize();
    } catch (err: any) {
      console.error('Failed to save reviewer:', err);
      setReviewerError(
        err?.message || 'Failed to save reviewer. Confirm that the user already has a QED account.'
      );
    } finally {
      setSavingReviewer(false);
    }
  }

  function renderReviewersTable(reviewersList: AdminReviewer[]) {
    if (reviewersList.length === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          No reviewers found.
        </div>
      );
    }

    return (
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Domains
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {reviewersList.map((reviewer) => (
            <tr key={reviewer.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                {reviewer.name}
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">
                {reviewer.email}
              </td>
              <td className="px-6 py-4 text-sm">
                <div className="flex flex-wrap gap-1.5">
                  {reviewer.domains.map((domain) => (
                    <span
                      key={domain}
                      className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                    >
                      {domain}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderReviewTable(
    reviews: ReviewRequest[],
    options?: {
      emptyMessage?: string;
      allowInlineStatusEdit?: boolean;
      projectSubtitle?: 'ownerId' | 'reviewQueue' | 'approvedDate';
    }
  ) {
    const emptyMessage = options?.emptyMessage || 'No projects found.';
    const allowInlineStatusEdit = options?.allowInlineStatusEdit === true;
    const projectSubtitle = options?.projectSubtitle || 'ownerId';

    if (reviews.length === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="overflow-hidden">
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="w-[140px] text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tags
              </th>
              <th className="w-[210px] text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted By
              </th>
              <th className="w-[150px] text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted
              </th>
              <th className="w-[130px] text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notes
              </th>
              <th className="w-[240px] text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Review State
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {reviews.map((review) => (
              <tr key={review.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm align-top">
                  <button
                    type="button"
                    onClick={() => handleOpenReviewProject(review)}
                    disabled={openingReviewProjectId === review.projectId}
                    className="flex w-full items-center gap-2 text-left font-medium text-primary hover:underline disabled:no-underline disabled:opacity-50"
                    title={review.projectTitle}
                  >
                    {openingReviewProjectId === review.projectId ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4 shrink-0" />
                    )}
                    <span className="min-w-0 truncate">{review.projectTitle}</span>
                  </button>
                  <div className="mt-1 truncate text-xs text-gray-500">
                    {projectSubtitle === 'reviewQueue'
                      ? formatReviewQueueSubtitle(review)
                      : projectSubtitle === 'approvedDate'
                      ? formatApprovedProjectSubtitle(review)
                      : review.ownerId}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm align-top">
                  {review.tags && review.tags.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {review.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                      <button
                        type="button"
                        onClick={() => openTagEditor(review.projectId, review.tags || [])}
                        className="inline-flex items-center justify-center h-5 w-5 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                        title="Edit tags"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openTagEditor(review.projectId, [])}
                      className="text-xs text-primary hover:text-primary-hover hover:underline"
                    >
                      + Add Tags
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 align-top">
                  <div className="truncate">{review.submitterName}</div>
                  <div className="mt-1 truncate text-xs text-gray-500">{review.ownerId}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 align-top">
                  {review.createdAt.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 align-top">
                  {review.noteEntries && review.noteEntries.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setViewingSubmitterNotes(review)}
                      className="font-medium text-primary hover:text-primary-hover hover:underline"
                    >
                      View notes
                    </button>
                  ) : (
                    <span className="text-gray-400">No notes</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right align-top">
                  {allowInlineStatusEdit ? (
                    editingStatusReviewId === review.id ? (
                      <div className="ml-auto flex items-center justify-end gap-2">
                        <select
                          value={review.status}
                          autoFocus
                          onBlur={() =>
                            setEditingStatusReviewId((currentId) =>
                              currentId === review.id ? null : currentId
                            )
                          }
                          onChange={(event) =>
                            handleQueueStatusSelect(
                              review,
                              event.target.value as ReviewStatus
                            )
                          }
                          disabled={updatingReviewId === review.id}
                          className="block w-full max-w-[220px] px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {formatStatus(status)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => setEditingStatusReviewId(null)}
                          className="inline-flex items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Cancel status edit"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="ml-auto flex items-center justify-end gap-3">
                        <span className="text-sm text-gray-700">{formatStatus(review.status)}</span>
                        <button
                          type="button"
                          onClick={() => setEditingStatusReviewId(review.id)}
                          disabled={updatingReviewId === review.id}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                      </div>
                    )
                  ) : (
                    <span className="text-sm text-gray-700">{formatStatus(review.status)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
            <span className="text-sm text-gray-400 ml-2">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Projects
            </button>
            <span className="text-sm text-gray-600">
              {currentUser?.displayName || currentUser?.email}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {busy ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={initialize}
              className="text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FolderOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {permissions.canViewAllProjects ? 'Total Projects' : 'Projects In Review'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {permissions.canViewAllProjects ? stats.totalProjects : projectsInReviewCount}
                  </p>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {permissions.canViewAllProjects ? 'Total Users' : 'Projects Needing Review'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {permissions.canViewAllProjects ? stats.totalUsers : projectsNeedingReviewCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-2">
              {permissions.canViewAllProjects && (
                <button
                  type="button"
                  onClick={() => setActiveTab('projects')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    activeTab === 'projects'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <FolderOpen className="h-4 w-4" />
                  Projects
                </button>
              )}
              {permissions.canViewPeople && (
                <button
                  type="button"
                  onClick={() => setActiveTab('people')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    activeTab === 'people'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <UserRound className="h-4 w-4" />
                  People
                </button>
              )}
              {permissions.canViewReviewers && (
                <button
                  type="button"
                  onClick={() => setActiveTab('reviewers')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    activeTab === 'reviewers'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Reviewers
                </button>
              )}
              {permissions.canViewReviewQueue && (
                <button
                  type="button"
                  onClick={() => setActiveTab('reviewQueue')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    activeTab === 'reviewQueue'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  Review Queue
                  {reviewQueue.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === 'reviewQueue'
                        ? 'bg-white/20 text-white'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {reviewQueue.length}
                    </span>
                  )}
                </button>
              )}
              {permissions.canViewApprovedProjects && (
                <button
                  type="button"
                  onClick={() => setActiveTab('approvedProjects')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    activeTab === 'approvedProjects'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Approved Assessment
                  {approvedProjects.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === 'approvedProjects'
                        ? 'bg-white/20 text-white'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {approvedProjects.length}
                    </span>
                  )}
                </button>
              )}
              {permissions.canViewApprovedProjects && (
                <button
                  type="button"
                  onClick={() => setActiveTab('completedProjects')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                    activeTab === 'completedProjects'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  Completed Projects
                  {completedProjects.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === 'completedProjects'
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {completedProjects.length}
                    </span>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/assistance', {
                  state: {
                    backTarget: {
                      pathname: '/admin',
                      state: { initialTab: activeTab },
                    },
                  },
                })}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <MessageSquare className="h-4 w-4" />
                AI Assistance
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'projects'
                    ? 'Recent Projects'
                    : activeTab === 'people'
                    ? 'People'
                    : activeTab === 'reviewers'
                    ? 'Reviewers'
                    : activeTab === 'approvedProjects'
                    ? 'Approved Assessment'
                    : activeTab === 'completedProjects'
                    ? 'Completed Projects'
                    : 'Projects Needing Review'}
                </h2>
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                  <div className="relative w-full sm:min-w-[320px] sm:max-w-sm">
                    <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={tableSearch}
                      onChange={(event) => setTableSearch(event.target.value)}
                      placeholder={
                        activeTab === 'projects'
                          ? 'Search projects'
                          : activeTab === 'people'
                          ? 'Search people'
                          : activeTab === 'reviewers'
                          ? 'Search reviewers'
                          : activeTab === 'approvedProjects'
                          ? 'Search approved projects'
                          : activeTab === 'completedProjects'
                          ? 'Search completed projects'
                          : 'Search review queue'
                      }
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  {activeTab === 'reviewers' && permissions.canViewReviewers && (
                    <button
                      type="button"
                      onClick={() => {
                        setReviewerError(null);
                        setShowAddReviewerModal(true);
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
                    >
                      <Plus className="h-4 w-4" />
                      Add Reviewer
                    </button>
                  )}
                </div>
              </div>
              {(activeTab === 'projects'
                || activeTab === 'reviewQueue'
                || activeTab === 'approvedProjects'
                || activeTab === 'completedProjects') && allTags.length > 0 && (
                <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4 text-gray-400 shrink-0" />
                  <button
                    type="button"
                    onClick={() => setTagFilter(null)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      tagFilter === null
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        tagFilter === tag
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
              {activeTab === 'projects' && permissions.canViewAllProjects && filteredProjects.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  No projects found.
                </div>
              ) : activeTab === 'projects' && permissions.canViewAllProjects ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project Title
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Owner
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tags
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reviews Enabled
                      </th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Main.tex Size
                      </th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredProjects.map((project) => (
                      <tr
                        key={project.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/project/${project.id}`)}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-primary hover:underline">
                          {project.title}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {project.ownerName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {project.updatedAt
                            ? new Date(project.updatedAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {project.tags && project.tags.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1">
                              {project.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {tag}
                                </span>
                              ))}
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openTagEditor(project.id, project.tags || []);
                                }}
                                className="inline-flex items-center justify-center h-5 w-5 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                                title="Edit tags"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openTagEditor(project.id, []);
                              }}
                              className="text-xs text-primary hover:text-primary-hover hover:underline"
                            >
                              + Add Tags
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {project.reviewEnabled ? 'Yes' : 'No'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-right">
                          {formatSize(project.mainTexSize)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="relative inline-block text-left">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setActiveProjectMenu(
                                  activeProjectMenu === project.id ? null : project.id
                                );
                              }}
                              className="inline-flex items-center justify-center p-2 text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            {activeProjectMenu === project.id && (
                              <div
                                className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={(event) =>
                                    handleCopyProject(event, project.id, project.title)
                                  }
                                  disabled={copyingProjectId === project.id}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {copyingProjectId === project.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Copy className="h-4 w-4" />
                                  )}
                                  Copy to my account
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) =>
                                    handleSetProjectReviewsEnabled(
                                      event,
                                      project.id,
                                      !project.reviewEnabled
                                    )
                                  }
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Shield className="h-4 w-4" />
                                  {project.reviewEnabled ? 'Disable Reviews' : 'Enable Reviews'}
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : activeTab === 'people' && permissions.canViewPeople ? (
                <>
                  {filteredPeople.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      No people found.
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Login
                          </th>
                          <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Projects
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredPeople.map((person) => (
                          <tr key={person.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {person.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {person.email}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {formatLastLogin(person)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 text-right">
                              {person.projectCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="flex flex-col gap-4 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-600">
                        {people.length > 0
                          ? `Showing ${peopleRangeStart}-${peopleRangeEnd} of ${stats.totalUsers}`
                          : 'No people to display'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Ordered by last sign in, newest first.
                      </p>
                      {peopleError && (
                        <p className="mt-1 text-xs text-red-600">{peopleError}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <button
                        type="button"
                        onClick={loadPreviousPeoplePage}
                        disabled={peopleLoading || !canLoadPreviousPeoplePage}
                        className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="min-w-[64px] text-center text-sm text-gray-500">
                        Page {peoplePageIndex + 1}
                      </span>
                      <button
                        type="button"
                        onClick={loadNextPeoplePage}
                        disabled={peopleLoading || !canLoadNextPeoplePage}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {peopleLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                        Next
                      </button>
                    </div>
                  </div>
                </>
              ) : activeTab === 'reviewers' && permissions.canViewReviewers ? (
                renderReviewersTable(filteredReviewers)
              ) : activeTab === 'approvedProjects' && permissions.canViewApprovedProjects ? (
                renderReviewTable(filteredApprovedProjects, {
                  emptyMessage: 'No approved assessments found.',
                  allowInlineStatusEdit: true,
                  projectSubtitle: 'approvedDate',
                })
              ) : activeTab === 'completedProjects' && permissions.canViewApprovedProjects ? (
                renderReviewTable(filteredCompletedProjects, {
                  emptyMessage: 'No completed projects found.',
                })
              ) : (
                renderReviewTable(filteredReviewQueue, {
                  emptyMessage: 'No projects are currently waiting for review.',
                  allowInlineStatusEdit: true,
                  projectSubtitle: 'reviewQueue',
                })
              )}
            </div>
          </>
        )}
      </main>

      {editingTagsFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit Tags</h2>
              <button
                type="button"
                onClick={() => { setEditingTagsFor(null); setTagsDraft(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (comma separated)
            </label>
            <input
              type="text"
              value={tagsDraft}
              onChange={(event) => setTagsDraft(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleSaveTags()}
              placeholder="e.g. Physics, Math, Biology"
              autoFocus
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mb-2"
            />
            <p className="text-xs text-gray-500 mb-4">
              Separate multiple tags with commas. Leave empty to remove all tags.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setEditingTagsFor(null); setTagsDraft(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTags}
                disabled={savingTags}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {savingTags && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddReviewerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Add Reviewer</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Reviewers must already have a QED account. Domain tags will be used for routing
                  review assignments later.
                </p>
              </div>
              <button
                type="button"
                onClick={resetReviewerModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close add reviewer modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  type="email"
                  value={reviewerEmailDraft}
                  onChange={(event) => setReviewerEmailDraft(event.target.value)}
                  placeholder="reviewer@example.com"
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Review domains
                </label>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_DOMAINS.map((domain) => {
                    const selected = reviewerDomainsDraft.includes(domain);
                    return (
                      <button
                        key={domain}
                        type="button"
                        onClick={() => toggleReviewerDomain(domain)}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {domain}
                      </button>
                    );
                  })}
                </div>
              </div>

              {reviewerError && (
                <p className="text-sm text-red-600">{reviewerError}</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={resetReviewerModal}
                className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddReviewer}
                disabled={savingReviewer}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingReviewer && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Reviewer
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingSubmitterNotes && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingSubmitterNotes(null)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-2xl shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900">Review Notes</h2>
                <p className="mt-1 truncate text-sm text-gray-600">
                  {viewingSubmitterNotes.projectTitle}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Submitted by {viewingSubmitterNotes.submitterName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewingSubmitterNotes(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
              {viewingNoteEntries.length > 0 ? (
                <div className="space-y-4">
                  {viewingNoteEntries.map((note, index) => (
                    <div
                      key={`${note.source}-${note.createdAt.getTime()}-${index}`}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                    >
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {note.source === 'submission' ? 'Submission note' : 'Reviewer note'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {note.authorName}
                            {note.source === 'reviewer' ? ` • ${formatStatus(note.status)}` : ''}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatNoteTimestamp(note.createdAt)}
                        </p>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700">
                        {note.text}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No notes available.</p>
              )}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setViewingSubmitterNotes(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingStatusChange && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Update Review Status
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Set <span className="font-medium text-gray-900">{pendingStatusChange.projectTitle}</span> to{' '}
              <span className="font-medium text-gray-900">{formatStatus(pendingStatusChange.status)}</span>.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={adminNotesDraft}
              onChange={(event) => setAdminNotesDraft(event.target.value)}
              placeholder="Optional notes for the author. These will be written to review_notes.txt."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setEditingStatusReviewId(null);
                  setPendingStatusChange(null);
                  setAdminNotesDraft('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmStatusChange}
                disabled={updatingReviewId === pendingStatusChange.reviewId}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {updatingReviewId === pendingStatusChange.reviewId && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
